import React, { useState, useEffect } from "react";
import { Modal, Tag, Typography, Select, Spin, notification } from "antd";
import RoleApi from "../common/Apis/RoleApi";
import UserApi from "../common/Apis/UserApi";

const { Text } = Typography;

const InviteUsersModal = ({ visible, onCancel, onInvite, subTenantId }) => {
    const [emails, setEmails] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState("");
    const [dropdownValue, setDropdownValue] = useState(null);
    const [dropdownError, setDropdownError] = useState("");
    const [loadingRoles, setLoadingRoles] = useState(false);

    // Initialize directly - FIXED
    const [listRoles, setListRoles] = useState([
        {id: 1, name: 'Super Admin', code: 'SUPER_ADMIN'}, 
        {id: 3, name: 'Account Admin', code: 'TENANT_ADMIN'}, 
        {id: 4, name: 'Store Admin', code: 'SUB_TENANT_ADMIN'},
        {id: 6, name: 'Manager', code: 'MANAGER'}, 
        {id: 7, name: 'Supervisor', code: 'SUPERVISOR'}, 
        {id: 2, name: 'Basic User', code: 'SYSTEM_USER'}, 
    ]);

    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

    // Reset form when modal opens
    useEffect(() => {
        if (visible) {
            setEmails([]);
            setInputValue("");
            setError("");
            setDropdownValue(null);
            setDropdownError("");
        }
    }, [visible]);

    // Filter roles based on user role
    const filteredRoles = listRoles.filter((role) => {
        if (decodedToken.roles[0] === "SUPER_ADMIN") {
            return true;
        } else if (decodedToken.roles[0] === "TENANT_ADMIN") {
            return role.code !== "SUPER_ADMIN" && role.code !== "TENANT_ADMIN";
        } else if (decodedToken.roles[0] === "SUB_TENANT_ADMIN") {
            return role.code !== "SUPER_ADMIN" && role.code !== "TENANT_ADMIN";
        }
        return true; // fallback
    });

   // Email validation
    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Add email on space or enter
    const handleKeyDown = (e) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            const email = inputValue.trim();

            if (email) {
                if (!isValidEmail(email)) {
                    setError("Invalid email format");
                } else if (emails.includes(email)) {
                    setError("This email is already added");
                } else {
                    setEmails([...emails, email]);
                    setError("");
                }
                setInputValue("");
            }
        }
    };

    const removeEmail = (email) => {
        setEmails(emails.filter((item) => item !== email));
    };

    const handleOk = () => {
        let hasError = false;

        if (emails.length === 0) {
            setError("Please add at least one email");
            hasError = true;
        }
        if (!dropdownValue) {
            setDropdownError("Please select a role");
            hasError = true;
        }

        if (hasError) return;

        setError("");
        setDropdownError("");

        // Prepare payload
        let payload = {
            emailList: emails,
            tenantDTO: {
                id: decodedToken.tenantId
            },
            subTenantDTO: {
                id: subTenantId
            },
            roleDTO: {
                id: dropdownValue
            }
        };

        // Call API
        UserApi.sendAptitudeTest(JSON.stringify(payload))
            .then((res) => {
                notification.open({
                    message: <span style={{ fontWeight: "bold" }}>Aptitude test sent successfully!</span>,
                    closeIcon: false,
                    duration: 3,
                    style: {
                        backgroundColor: "#b3ffb3",
                        color: "green",
                    },
                });
                setEmails([]);
                setDropdownValue(null);
                onInvite && onInvite(payload);
            })
            .catch((err) => {
                console.log(err);
                notification.error({
                    message: err?.message || "Failed to send aptitude test",
                });
            });
    };

    const isInviteDisabled =
        emails.length === 0 || !!error || !dropdownValue;

    return (
        <Modal
            title={
                <div>
                    Invite Users
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Type an email and press <b>Enter</b> or <b>Space</b> to add it
                    </Text>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            onOk={handleOk}
            okText="Invite"
            okButtonProps={{ disabled: isInviteDisabled }}
        >
            {/* Email Input */}
            <div>
                <div
                    style={{
                        border: `1px solid ${error ? "#ff4d4f" : "#d9d9d9"}`,
                        borderRadius: 4,
                        padding: 8,
                        minHeight: 40,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                    }}
                >
                    {emails.map((email) => (
                        <Tag
                            key={email}
                            closable
                            onClose={() => removeEmail(email)}
                        >
                            {email}
                        </Tag>
                    ))}
                    <input
                        style={{
                            border: "none",
                            outline: "none",
                            flex: 1,
                            minWidth: 120,
                        }}
                        placeholder="Type email here..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                {error && (
                    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
                        {error}
                    </div>
                )}
            </div>

            {/* Dropdown Field */}
            <div style={{ marginTop: 12 }}>
                {loadingRoles ? (
                    <Spin style={{ width: "100%" }} />
                ) : (
                    <Select
                        placeholder="Select a role"
                        style={{ width: "100%", minHeight: 40 }}
                        value={dropdownValue}
                        onChange={(value) => {
                            setDropdownValue(value);
                            setDropdownError("");
                        }}
                        options={filteredRoles.map((role) => ({
                            label: role.name,
                            value: role.id,
                        }))}
                    />
                )}
                {dropdownError && (
                    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
                        {dropdownError}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default InviteUsersModal;
